import { create } from "zustand";

interface IImportStatusValues {
  file: File | null; // The file to upload
  fileName: string; // The name of the file
  zipFile: File | null; // The zip file to upload
  zipFileName: string; // The name of the zip file
  groupId?: string; // The group ID to import the candidates into
  statusId?: string; // Filled with either "OptimisticImportId" or the GUID of the import status
  isInputError: boolean; // Set when the input is invalid
  inputErrorMessage: string; // Error message to display when the input is invalid
  includeZipFile?: boolean; // Toggle the visibility of the zip file input
  isZipInputError?: boolean; // Set when the zip input is invalid
  zipInputErrorMessage?: string; // Error message to display when the zip input is invalid
  isUploadSuccess: boolean; // Set when the upload is successful
  isUploadError: boolean; // Set when the upload fails
  uploadErrorMessage: string; // Error message to display when the upload fails
  showImportModal: boolean; // Toggle the visibility of the import modal
  showImportStatusText: boolean; // Show text on the Manage Candidates page
  showImportStatusNotification: boolean; // Show notification on any other page
}

interface IImportStatusActions {
  isValidId: () => boolean; // Checks if the statusId has a value and is not the default value
  setFile: (value: File | null, name: string) => void;
  setZipFile: (value: File | null, name: string) => void;
  setGroupId: (value: string | undefined) => void;
  setStatusId: (value: string | undefined) => void;
  setInputError: (value: boolean, message: string) => void;
  setIncludeZipFile: (value: boolean) => void;
  setZipInputError: (value: boolean, message: string) => void;
  setUploadError: (value: boolean, message: string) => void;
  setUploadSuccess: (value: boolean) => void;
  setShowImportModal: (value: boolean) => void;
  setShowImportStatusText: (value: boolean) => void;
  setShowImportStatusNotification: (value: boolean) => void;
  reset: () => void;
  resetZip: () => void;
  resetUpload: () => void;
}

type ImportStatusStore = IImportStatusValues & IImportStatusActions;

const initialStoreValues: IImportStatusValues = {
  file: null,
  fileName: "",
  zipFile: null,
  zipFileName: "",
  statusId: undefined,
  groupId: undefined,
  isInputError: false,
  inputErrorMessage: "",
  includeZipFile: false,
  isZipInputError: false,
  zipInputErrorMessage: "",
  isUploadSuccess: false,
  isUploadError: false,
  uploadErrorMessage: "",
  showImportModal: false,
  showImportStatusText: false,
  showImportStatusNotification: false,
};

export const useImportStatusStore = create<ImportStatusStore>((set, get) => ({
  ...initialStoreValues,
  isValidId: () => {
    const currentId = get().statusId;
    return !!currentId && currentId !== "00000000-0000-0000-0000-000000000000";
  },
  setFile: (value, name) => set({ file: value, fileName: name }),
  setZipFile: (value, name) => set({ zipFile: value, zipFileName: name }),
  setGroupId: (value) => set({ groupId: value }),
  setStatusId: (value) => set({ statusId: value }),
  setInputError: (value, message) => set({ isInputError: value, inputErrorMessage: message }),
  setUploadError: (value, message) => set({ isUploadError: value, uploadErrorMessage: message }),
  setUploadSuccess: (value) => set({ isUploadSuccess: value, isUploadError: !value, isInputError: !value }),
  setIncludeZipFile: (value) => set({ includeZipFile: value }),
  setZipInputError: (value, message) => set({ isZipInputError: value, zipInputErrorMessage: message }),
  setShowImportModal: (value) => set({ showImportModal: value }),
  setShowImportStatusText: (value) => set({ showImportStatusText: value }),
  setShowImportStatusNotification: (value) => set({ showImportStatusNotification: value }),
  reset: () => {
    set({ ...initialStoreValues, showImportModal: get().showImportModal });
  },
  resetZip: () => set({ zipFile: null, zipFileName: "" }),
  resetUpload: () =>
    set({
      isUploadSuccess: false,
      isUploadError: false,
      uploadErrorMessage: "",
      file: null,
      fileName: "",
      zipFile: null,
      zipFileName: "",
    }),
}));
